"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import LoginSignup from "@/containers/website/profile/loginSignup/LoginSignup";
import ProfileModal from "@/containers/website/profile/ProfileModal";
import staticData from "@/public/data/landing.json";
import logo from "@/public/images/logo-persian.webp";
import PhosphorIcon, {
  type PhosphorIconName,
} from "@/shared/components/common/PhosphorIcon";
import fetcher from "@/shared/libs/fetcher";
import useAuthToken, {
  AUTH_TOKEN_CHANGED_EVENT,
} from "@/shared/hooks/useAuthToken";
import { convertToEnglishDigits, setCookie } from "@/shared/libs/utils";
import { useGlobalStore } from "@/shared/stores/global-store/GlobalStoreProvider";
import type {
  SettingsResponse,
  WebsiteSettings,
} from "@/types/services/public/apis/Settings";

type HeaderNavChild = {
  id?: string | number;
  label: string;
  link: string;
};

type HeaderNavItem = {
  id?: string | number;
  title: string;
  link: string;
  state?: "enabled" | "disabled";
  navItems?: HeaderNavChild[];
};

type HeaderSettings = WebsiteSettings & {
  header?: {
    nav?: {
      links?: HeaderNavItem[];
      support?: string;
    };
    btns?: Array<{
      name: string;
      text: string;
      link: string;
      id?: string;
    }>;
  };
  menus?: {
    header?: HeaderNavItem[];
  };
};

const FALLBACK_NAV_ITEMS = staticData.header.nav.links as HeaderNavItem[];
const FALLBACK_SUPPORT = staticData.header.nav.support;
const FALLBACK_MORE_ITEMS: HeaderNavChild[] = [
  { label: "درباره ما", link: "/about-us" },
  { label: "تماس با ما", link: "/help-center/telephone" },
  { label: "مجله گردشگری", link: "https://mdsafar.com/mag/" },
  { label: "آژانس‌ها و سازمان‌ها", link: "/collaboration-agencies" },
  // { label: "ثبت هتل و اقامتگاه", link: "/hotel" },
];

const NAV_ICON_BY_LINK: Record<string, PhosphorIconName> = {
  "/": "AirplaneTilt",
  "/flights": "AirplaneTilt",
  "/hotel": "Buildings",
  "/tour": "GlobeHemisphereEast",
};

const HEADER_FONT_CLASS = "text-[14.9px]";
const HEADER_ICON_SIZE = 14.9;
const HEADER_MOBILE_ICON_SIZE = 20;
const HEADER_MOBILE_MENU_ICON_SIZE = 18;
const HEADER_CONTROL_RADIUS = "rounded-[12px]";
const HEADER_CONTROL_RADIUS_STYLE = {
  borderRadius: 12,
};
const HEADER_SOFT_PRIMARY_BG = "bg-primary/10";
const HEADER_SOFT_PRIMARY_HOVER_BG = "hover:bg-primary/15";

function HeaderNavButton({
  href,
  onClick,
  disabled,
  className,
  style,
  children,
}: {
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  className: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  if (href && !disabled) {
    return (
      <Link href={href} onClick={onClick} className={className} style={style}>
        {children}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={style}
      type="button"
    >
      {children}
    </button>
  );
}

export default function ResultsV2Header() {
  const router = useRouter();
  const authToken = useAuthToken();
  const [active, setActive] = useState("رزرو هتل");
  const [settings, setSettings] = useState<HeaderSettings | null>(null);
  const [profileData, setProfileData] = useState<ProfileResult | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [hotelMenuOpen, setHotelMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileExpandedMenus, setMobileExpandedMenus] = useState<string[]>([
    "رزرو هتل",
    "رزرو هتل‌ها",
    "رزرو هتل ها",
  ]);
  const setIsLoginModalOn = useGlobalStore((state) => state.setIsLoginModalOn);

  useEffect(() => {
    let mounted = true;

    fetcher<SettingsResponse>("/api/settings/")
      .then((response) => {
        if (!mounted) return;
        setSettings((response.data?.results ?? null) as HeaderSettings | null);
      })
      .finally(() => {
        if (mounted) setSettingsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    if (!authToken) {
      setProfileData(null);
      return () => {
        mounted = false;
      };
    }

    fetcher<ProfileRes>("/api/profile/me")
      .then((response) => {
        if (!mounted) return;
        setProfileData(response.data?.results ?? null);
      })
      .catch(() => {
        if (mounted) setProfileData(null);
      });

    return () => {
      mounted = false;
    };
  }, [authToken]);

  useEffect(() => {
    document.body.classList.toggle("mdsafar-mobile-layer-open", drawerOpen);

    return () => {
      document.body.classList.remove("mdsafar-mobile-layer-open");
    };
  }, [drawerOpen]);

  const logoutAction = () => {
    setCookie("token", "", -1);
    setProfileData(null);
    window.dispatchEvent(new Event(AUTH_TOKEN_CHANGED_EVENT));
    router.refresh();
  };

  if (settingsLoading) return <ResultsV2HeaderSkeleton />;

  const navItems = getHeaderNavItems(settings);
  const moreItems = getMoreItems(settings);
  const supportPhone = getSupportPhone(settings);
  const logoSrc = settings?.theme?.header_logos || logo;

  return (
    <>
      <header
        className="border-b border-[#e5e7eb] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
        dir="rtl"
      >
        <div className="customContainer mx-auto max-md:!px-4">
          <div className="flex items-center justify-between gap-3 py-4">
            <Link
              href="/"
              className="inline-flex shrink-0"
              aria-label="بازگشت به صفحه اصلی"
            >
              <Image
                src={logoSrc}
                alt={settings?.theme?.site_name ?? "معین درباری"}
                className="h-[34px] w-auto shrink-0 object-contain"
                width={180}
                height={68}
                priority
              />
            </Link>

            <nav className="hidden flex-1 items-center gap-0.5 md:flex">
              {navItems.map((item) => {
                const hasSubMenu = Boolean(item.navItems?.length);

                return (
                  <div
                    key={item.id ?? item.link}
                    className="relative"
                    onMouseEnter={() =>
                      hasSubMenu ? setHotelMenuOpen(true) : undefined
                    }
                    onMouseLeave={() =>
                      hasSubMenu ? setHotelMenuOpen(false) : undefined
                    }
                  >
                    <HeaderNavButton
                      href={hasSubMenu ? undefined : item.link}
                      onClick={() => setActive(item.title)}
                      disabled={item.state === "disabled"}
                      className={`flex items-center gap-1.5 ${HEADER_CONTROL_RADIUS} px-3 py-2 ${HEADER_FONT_CLASS} text-foreground transition-all ${HEADER_SOFT_PRIMARY_HOVER_BG} hover:text-primary`}
                      style={HEADER_CONTROL_RADIUS_STYLE}
                    >
                      <PhosphorIcon
                        name={getNavIcon(item.link)}
                        size={HEADER_ICON_SIZE}
                        className="text-muted-foreground"
                      />
                      <span className="font-normal">{item.title}</span>
                      {hasSubMenu && (
                        <PhosphorIcon
                          name="CaretDown"
                          size={HEADER_ICON_SIZE}
                          className={`transition-transform ${
                            hotelMenuOpen
                              ? "rotate-180 text-primary"
                              : "text-muted-foreground"
                          }`}
                        />
                      )}
                    </HeaderNavButton>

                    {hasSubMenu && hotelMenuOpen && (
                      <div className="absolute right-0 top-full z-50 min-w-[200px] overflow-hidden rounded-2xl border border-border bg-white py-2 shadow-xl">
                        {item.navItems?.map((navItem) => (
                          <a
                            key={navItem.id ?? navItem.link}
                            href={navItem.link}
                            className={`flex w-full items-center px-5 py-2.5 text-right ${HEADER_FONT_CLASS} text-slate-950 transition ${HEADER_SOFT_PRIMARY_HOVER_BG} hover:text-primary`}
                          >
                            {navItem.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="relative" onMouseLeave={() => setMoreOpen(false)}>
                <button
                  onMouseEnter={() => setMoreOpen(true)}
                  onClick={() => setMoreOpen((value) => !value)}
                  className={`flex items-center gap-1 ${HEADER_CONTROL_RADIUS} px-3 py-2 ${HEADER_FONT_CLASS} transition-all ${
                    moreOpen
                      ? `${HEADER_SOFT_PRIMARY_BG} text-primary`
                      : `text-foreground ${HEADER_SOFT_PRIMARY_HOVER_BG}`
                  }`}
                  style={HEADER_CONTROL_RADIUS_STYLE}
                  type="button"
                >
                  بیشتر
                  <PhosphorIcon
                    name="CaretDown"
                    size={HEADER_ICON_SIZE}
                    className={`transition-transform ${
                      moreOpen
                        ? "rotate-180 text-primary"
                        : "text-muted-foreground"
                    }`}
                  />
                </button>

                {moreOpen && (
                  <div className="absolute right-0 top-full z-50 mt-1 min-w-[190px] overflow-hidden rounded-2xl border border-border bg-white py-2 shadow-xl">
                    {moreItems.map((item) => (
                      <a
                        key={item.id ?? item.link}
                        href={item.link}
                        className={`flex w-full items-center px-5 py-2.5 text-right ${HEADER_FONT_CLASS} text-slate-950 transition ${HEADER_SOFT_PRIMARY_HOVER_BG} hover:text-primary`}
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            <div className="hidden shrink-0 items-center gap-2 md:flex">
              {/* <button
                onClick={() => setSearchOpen(true)}
                className={`grid h-10 w-10 place-items-center ${HEADER_CONTROL_RADIUS} border border-border bg-white text-muted-foreground shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition hover:border-primary hover:text-primary`}
                style={HEADER_CONTROL_RADIUS_STYLE}
                title="جستجوی هتل"
                type="button"
              >
                <PhosphorIcon name="MagnifyingGlass" size={HEADER_ICON_SIZE} />
              </button> */}
              <a
                href={`tel:${normalizePhoneForHref(supportPhone)}`}
                className={`flex h-10 items-center gap-2 ${HEADER_CONTROL_RADIUS} border border-border bg-white px-3 py-2 text-foreground shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition hover:border-primary hover:text-primary`}
                style={HEADER_CONTROL_RADIUS_STYLE}
              >
                <PhosphorIcon
                  name="Phone"
                  size={HEADER_ICON_SIZE}
                  className="shrink-0 text-primary"
                />
                <span className={`${HEADER_FONT_CLASS} font-medium`}>
                  {supportPhone}
                </span>
              </a>
              {profileData ? (
                <ProfileModal
                  profileData={profileData}
                  logoutAction={logoutAction}
                >
                  <button
                    className="flex h-10 items-center gap-2 rounded-full border border-border bg-white px-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition hover:border-primary hover:bg-primary/5"
                    style={{ borderRadius: 9999 }}
                    type="button"
                    aria-label="حساب کاربری"
                  >
                    <PhosphorIcon
                      name="User"
                      size={HEADER_ICON_SIZE}
                      className="text-primary"
                    />
                    <span
                      className={`${HEADER_FONT_CLASS} max-w-[130px] truncate font-medium text-foreground`}
                    >
                      {profileData.name ?? profileData.phone}
                    </span>
                  </button>
                </ProfileModal>
              ) : (
                <LoginSignup>
                  <button
                    className="flex h-10 items-center gap-2 rounded-full border border-border bg-white px-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition hover:border-primary hover:bg-primary/5"
                    style={{ borderRadius: 9999 }}
                    type="button"
                    aria-label="ورود یا ثبت‌نام"
                  >
                    <PhosphorIcon
                      name="User"
                      size={HEADER_ICON_SIZE}
                      className="text-primary"
                    />
                    <span
                      className={`${HEADER_FONT_CLASS} font-medium text-foreground`}
                    >
                      ورود
                    </span>
                  </button>
                </LoginSignup>
              )}
            </div>

            <div className="flex items-center gap-1 md:hidden">
              {/* <button
                onClick={() => setSearchOpen(true)}
                className={`grid h-10 w-10 place-items-center ${HEADER_CONTROL_RADIUS} text-slate-500 transition ${HEADER_SOFT_PRIMARY_HOVER_BG} hover:text-primary`}
                style={HEADER_CONTROL_RADIUS_STYLE}
                type="button"
                aria-label="جستجو"
              >
                <PhosphorIcon name="MagnifyingGlass" size={HEADER_ICON_SIZE} />
              </button> */}
              <a
                href={`tel:${normalizePhoneForHref(supportPhone)}`}
                className={`grid h-10 w-10 place-items-center ${HEADER_CONTROL_RADIUS} text-slate-500 transition ${HEADER_SOFT_PRIMARY_HOVER_BG} hover:text-primary`}
                style={HEADER_CONTROL_RADIUS_STYLE}
                aria-label="تماس"
              >
                <PhosphorIcon
                  name="Phone"
                  size={HEADER_MOBILE_ICON_SIZE}
                  weight="bold"
                />
              </a>
              {profileData ? (
                <ProfileModal
                  profileData={profileData}
                  logoutAction={logoutAction}
                >
                  <button
                    className={`grid h-10 w-10 place-items-center rounded-full transition ${HEADER_SOFT_PRIMARY_HOVER_BG}`}
                    style={{ borderRadius: 9999 }}
                    type="button"
                    aria-label="حساب کاربری"
                  >
                    <PhosphorIcon
                      name="User"
                      size={HEADER_MOBILE_ICON_SIZE}
                      weight="bold"
                      className="text-primary"
                    />
                  </button>
                </ProfileModal>
              ) : (
                <button
                  onClick={() => setIsLoginModalOn(true)}
                  className={`grid h-10 w-10 place-items-center rounded-full transition ${HEADER_SOFT_PRIMARY_HOVER_BG}`}
                  style={{ borderRadius: 9999 }}
                  type="button"
                  aria-label="ورود"
                >
                  <PhosphorIcon
                    name="User"
                    size={HEADER_MOBILE_ICON_SIZE}
                    weight="bold"
                    className="text-primary"
                  />
                </button>
              )}
              <button
                onClick={() => setDrawerOpen(true)}
                className={`grid h-10 w-10 place-items-center ${HEADER_CONTROL_RADIUS} transition ${HEADER_SOFT_PRIMARY_HOVER_BG}`}
                style={HEADER_CONTROL_RADIUS_STYLE}
                type="button"
                aria-label="منو"
              >
                <PhosphorIcon
                  name="List"
                  size={HEADER_MOBILE_ICON_SIZE}
                  weight="bold"
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-[60] flex justify-start md:hidden"
          dir="rtl"
        >
          <button
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            type="button"
            aria-label="بستن منو"
          />
          <div className="relative flex h-full w-72 flex-col overflow-hidden bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <Image
                src={logoSrc}
                alt={settings?.theme?.site_name ?? "معین درباری"}
                width={150}
                height={56}
                className="h-7 w-auto"
              />
              <button
                onClick={() => setDrawerOpen(false)}
                className="grid size-8 place-items-center rounded-full bg-muted transition hover:bg-secondary"
                type="button"
                aria-label="بستن"
              >
                <PhosphorIcon
                  name="X"
                  size={HEADER_MOBILE_MENU_ICON_SIZE}
                  weight="bold"
                />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-3">
              {navItems.map((item) => {
                const menuKey = item.title;
                const hasChildren = Boolean(item.navItems?.length);
                const isExpanded = mobileExpandedMenus.includes(menuKey);

                return (
                  <div key={item.id ?? item.link}>
                    <HeaderNavButton
                      href={hasChildren ? undefined : item.link}
                      onClick={() => {
                        setActive(item.title);
                        if (hasChildren) {
                          setMobileExpandedMenus((current) =>
                            current.includes(menuKey)
                              ? current.filter((key) => key !== menuKey)
                              : [...current, menuKey],
                          );
                          return;
                        }

                        if (!hasChildren) {
                          setDrawerOpen(false);
                        }
                      }}
                      className={`mb-1 flex w-full items-center justify-between gap-3 ${HEADER_CONTROL_RADIUS} px-4 py-3 ${HEADER_FONT_CLASS} transition-all ${
                        active === item.title
                          ? `${HEADER_SOFT_PRIMARY_BG} font-semibold text-primary`
                          : `text-slate-950 ${HEADER_SOFT_PRIMARY_HOVER_BG}`
                      }`}
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <PhosphorIcon
                          name={getNavIcon(item.link)}
                          size={HEADER_MOBILE_MENU_ICON_SIZE}
                          weight="bold"
                          className={
                            active === item.title
                              ? "text-primary"
                              : "text-slate-500"
                          }
                        />
                        <span className="truncate">{item.title}</span>
                      </span>
                      {hasChildren && (
                        <PhosphorIcon
                          name="CaretDown"
                          size={HEADER_MOBILE_MENU_ICON_SIZE}
                          weight="bold"
                          className={`shrink-0 text-slate-500 transition-transform ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      )}
                    </HeaderNavButton>

                    {hasChildren && isExpanded && (
                      <div className="mb-2 mr-5 border-r border-primary/15 pr-2">
                        {item.navItems?.map((navItem) => (
                          <a
                            key={navItem.id ?? navItem.link}
                            href={navItem.link}
                            onClick={() => setDrawerOpen(false)}
                            className={`mb-0.5 flex w-full items-center ${HEADER_CONTROL_RADIUS} px-4 py-2.5 text-right ${HEADER_FONT_CLASS} text-slate-700 transition ${HEADER_SOFT_PRIMARY_HOVER_BG} hover:text-primary`}
                          >
                            {navItem.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="mx-2 my-3 h-px bg-border" />
              <p className="mb-2 px-4 text-[14.9px] font-semibold text-slate-500">
                بیشتر
              </p>
              {moreItems.map((item) => (
                <a
                  key={item.id ?? item.link}
                  href={item.link}
                  className={`mb-0.5 flex w-full items-center ${HEADER_CONTROL_RADIUS} px-4 py-2.5 text-right ${HEADER_FONT_CLASS} text-slate-950 transition ${HEADER_SOFT_PRIMARY_HOVER_BG}`}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* {searchOpen && <HotelSearchModal onClose={() => setSearchOpen(false)} />} */}
    </>
  );
}

function ResultsV2HeaderSkeleton() {
  const navItems = FALLBACK_NAV_ITEMS;
  const supportPhone = FALLBACK_SUPPORT;

  return (
    <header
      className="border-b border-[#e5e7eb] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      dir="rtl"
    >
      <div className="customContainer mx-auto max-md:!px-4">
        <div className="flex items-center justify-between gap-3 py-4">
          <Image
            src={logo}
            alt="معین درباری"
            className="h-[34px] w-auto shrink-0 object-contain opacity-90"
            width={180}
            height={68}
            priority
          />

          <nav className="hidden flex-1 items-center gap-0.5 md:flex">
            {navItems.map((item) => (
              <button
                key={item.id ?? item.link}
                className={`flex cursor-default items-center gap-1.5 ${HEADER_CONTROL_RADIUS} px-3 py-2 ${HEADER_FONT_CLASS} transition-all ${
                  item.link.includes("hotel")
                    ? `${HEADER_SOFT_PRIMARY_BG} text-primary`
                    : "text-foreground opacity-75"
                }`}
                style={HEADER_CONTROL_RADIUS_STYLE}
                type="button"
                disabled
              >
                <PhosphorIcon
                  name={getNavIcon(item.link)}
                  size={HEADER_ICON_SIZE}
                  className={
                    item.link.includes("hotel")
                      ? "text-primary"
                      : "text-muted-foreground"
                  }
                />
                <span
                  className={
                    item.link.includes("hotel")
                      ? "font-semibold"
                      : "font-normal"
                  }
                >
                  {item.title}
                </span>
                {Boolean(item.navItems?.length) && (
                  <PhosphorIcon
                    name="CaretDown"
                    size={HEADER_ICON_SIZE}
                    className="text-muted-foreground"
                  />
                )}
              </button>
            ))}
            <button
              className={`flex cursor-default items-center gap-1 ${HEADER_CONTROL_RADIUS} px-3 py-2 ${HEADER_FONT_CLASS} text-foreground opacity-75`}
              style={HEADER_CONTROL_RADIUS_STYLE}
              type="button"
              disabled
            >
              بیشتر
              <PhosphorIcon
                name="CaretDown"
                size={HEADER_ICON_SIZE}
                className="text-muted-foreground"
              />
            </button>
          </nav>

          <div className="hidden shrink-0 items-center gap-2 md:flex">
            {/* <button
              className={`grid h-10 w-10 cursor-default place-items-center ${HEADER_CONTROL_RADIUS} border border-border bg-white text-muted-foreground shadow-[0_1px_2px_rgba(15,23,42,0.03)]`}
              style={HEADER_CONTROL_RADIUS_STYLE}
              type="button"
              disabled
            >
              <PhosphorIcon name="MagnifyingGlass" size={HEADER_ICON_SIZE} />
            </button> */}
            <button
              className={`flex h-10 cursor-default items-center gap-2 ${HEADER_CONTROL_RADIUS} border border-border bg-white px-3 py-2 text-foreground shadow-[0_1px_2px_rgba(15,23,42,0.03)]`}
              style={HEADER_CONTROL_RADIUS_STYLE}
              type="button"
              disabled
            >
              <PhosphorIcon
                name="Phone"
                size={HEADER_ICON_SIZE}
                className="shrink-0 text-primary"
              />
              <span className={`${HEADER_FONT_CLASS} font-medium`}>
                {supportPhone}
              </span>
            </button>
            <button
              className={`grid h-10 w-10 cursor-default place-items-center rounded-full border border-border ${HEADER_SOFT_PRIMARY_BG} shadow-[0_1px_2px_rgba(15,23,42,0.03)]`}
              style={{ borderRadius: 9999 }}
              type="button"
              disabled
            >
              <PhosphorIcon
                name="User"
                size={HEADER_ICON_SIZE}
                className="text-primary"
              />
            </button>
          </div>

          <div className="flex items-center gap-1 md:hidden">
            {(["Phone", "User", "List"] as PhosphorIconName[]).map((icon) => (
              <button
                key={icon}
                className={`grid h-10 w-10 cursor-default place-items-center ${
                  icon === "User" ? "rounded-full" : HEADER_CONTROL_RADIUS
                } text-slate-500 ${HEADER_SOFT_PRIMARY_BG}`}
                style={
                  icon === "User"
                    ? { borderRadius: 9999 }
                    : HEADER_CONTROL_RADIUS_STYLE
                }
                type="button"
                disabled
              >
                <PhosphorIcon
                  name={icon}
                  size={HEADER_MOBILE_ICON_SIZE}
                  weight="bold"
                  className={icon === "User" ? "text-primary" : undefined}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

function getHeaderNavItems(settings: HeaderSettings | null): HeaderNavItem[] {
  const navItems =
    settings?.header?.nav?.links ??
    settings?.menus?.header ??
    FALLBACK_NAV_ITEMS;

  return navItems
    .filter((item) => item.title !== "بلاگ")
    .map((item) => {
      if (item.title === "بلیط هواپیما") return { ...item, link: "/flight" };
      if (item.title === "رزرو هتل") {
        return { ...item, link: "/hotel", navItems: undefined };
      }
      if (item.title === "رزرو تور") return { ...item, link: "/tour" };

      return item;
    });
}

function getMoreItems(settings: HeaderSettings | null): HeaderNavChild[] {
  const extraLinks = settings?.header?.btns
    ?.filter((item) => item.name !== "loginSignup")
    .map((item) => ({
      id: item.id ?? item.name,
      label: item.text,
      link: item.link,
    }));

  const items = extraLinks?.length ? extraLinks : FALLBACK_MORE_ITEMS;

  return items
    .filter((item) => item.label !== "ثبت هتل و اقامتگاه")
    .map((item) =>
      item.label === "مجله گردشگری"
        ? { ...item, link: "https://mdsafar.com/mag/" }
        : item,
    );
}

function getSupportPhone(settings: HeaderSettings | null) {
  const phone =
    settings?.header?.nav?.support ??
    settings?.support?.online_reserve?.phone ??
    settings?.support?.support_numbers?.find((item) => item.value)?.value ??
    FALLBACK_SUPPORT;

  return getPhoneDisplayValue(phone);
}

function getNavIcon(link: string): PhosphorIconName {
  const normalized =
    link === "/" ? "/" : `/${link.split("/").filter(Boolean)[0]}`;

  return NAV_ICON_BY_LINK[normalized] ?? "DotsThreeCircle";
}

function normalizePhoneForHref(phone: string) {
  return convertToEnglishDigits(phone).replace(/[^\d+]/g, "");
}

function getPhoneDisplayValue(phone: string) {
  const normalized = convertToEnglishDigits(phone);
  const match = normalized.match(/\+?\d[\d\s-]{5,}\d/);

  return match
    ? match[0].replace(/[\s-]/g, "")
    : phone.replace("پیگیری رزرو", "").trim();
}

function HotelSearchModal({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-16"
      dir="rtl"
    >
      <button
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        type="button"
        aria-label="بستن"
      />
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center gap-3 border-b border-border px-4 py-4">
          <PhosphorIcon
            name="MagnifyingGlass"
            size={HEADER_ICON_SIZE}
            className="shrink-0 text-primary"
          />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className={`min-w-0 flex-1 bg-transparent ${HEADER_FONT_CLASS} text-foreground outline-none placeholder:text-muted-foreground`}
            placeholder="نام هتل، شهر یا منطقه را جستجو کنید..."
          />
          <button
            onClick={onClose}
            className={`grid size-7 shrink-0 place-items-center ${HEADER_CONTROL_RADIUS} ${HEADER_SOFT_PRIMARY_BG} transition ${HEADER_SOFT_PRIMARY_HOVER_BG}`}
            type="button"
            aria-label="بستن"
          >
            <PhosphorIcon name="X" size={HEADER_ICON_SIZE} />
          </button>
        </div>
        <div className="px-5 py-8 text-center">
          <p className={`${HEADER_FONT_CLASS} font-semibold text-foreground`}>
            {query.trim()
              ? "برای جستجوی هتل، مقصد را در فرم اصلی انتخاب کنید."
              : "جستجوی سریع هتل به داده‌های واقعی متصل می‌شود."}
          </p>
          <p className={`mt-1 ${HEADER_FONT_CLASS} text-muted-foreground`}>
            این پنجره فعلاً فقط برای جستجوی عمومی سایت استفاده می‌شود.
          </p>
        </div>
        <div className="border-t border-border px-4 py-3">
          <button
            onClick={onClose}
            className={`flex w-full items-center justify-center gap-2 ${HEADER_CONTROL_RADIUS} bg-primary py-2.5 ${HEADER_FONT_CLASS} font-bold text-white transition hover:opacity-90`}
            type="button"
          >
            <PhosphorIcon name="MagnifyingGlass" size={HEADER_ICON_SIZE} />
            مشاهده همه نتایج
          </button>
        </div>
      </div>
    </div>
  );
}
